import { PostgrestError } from "@supabase/supabase-js";
import * as R from "ramda";

import { Artifact, ArtifactFilters, ArtifactQueryParams, ArtifactQueryResponse, ArtifactType } from "../../types/artifact";
import type { RankingOrder } from "../../types/callRanking";
import { DEFAULT_RANKING_COUNT } from "../../types/callRanking";
import { logError, normalizeError } from "../../utils/error";
import { getTimeRangeDbFilters, TimeRange } from "../../utils/timerange";
import { supabase } from "../supabase/client";

const ARTIFACT_BATCH_SIZE = 100;

export interface FetchResult<DataType> {
  data: DataType;
  error: PostgrestError | Error | null;
}

interface ApplyFiltersParams {
  query: any;
  filters?: ArtifactFilters;
  permittedEmails: string[] | null;
}

const applyFilters = ({ query, filters, permittedEmails }: ApplyFiltersParams) => {
  // Apply permitted emails filter if user has restrictions
  if (permittedEmails && permittedEmails.length > 0) {
    query = query.in("user_email", permittedEmails.map(email => email.toLowerCase()));
  }

  if (!filters) {
    return query;
  }

  Object.entries(filters).forEach(([key, value]) => {
    if (value === null || value === undefined || value === "all") {
      return;
    }

    if (key === "search") {
      // Apply full-text search using the tsvector column
      if (typeof value === "string" && value.trim() !== "") {
        // Format the search term for PostgreSQL tsquery
        const searchTerm = value.trim().split(/\s+/).join(" & ");
        query = query.textSearch("fts_content_en", searchTerm, {
          type: "plain",
          config: "english"
        });
      }
    } else if (key === "time_range") {
      const { created_from, created_to } = getTimeRangeDbFilters(value);
      if (created_from) {
        query = query.gte("created", created_from);
      }
      if (created_to) {
        query = query.lte("created", created_to);
      }
    } else if (key === "min_score") {
      // Apply greater than or equal filter on sc_overall_score
      query = query.gt("sc_overall_score", value);
    } else if (key === "max_score") {
      // Apply less than or equal filter on sc_overall_score
      query = query.lte("sc_overall_score", value);
    } else if (key === "scored_status") {
      if (value === "unscored") {
        query = query.is("sc_overall_score", null);
      } else if (value === "scored") {
        query = query.not("sc_overall_score", "is", null);
      }
    } else if (key === "call_type") {
      // Use the dedicated call_type field from the database view
      query = query.eq("call_type", value);
    } else if (key === "sc_name") {
      query = query.eq("sc_name", value);
    } else if (key === "pp_process_decision") {
      if (value != "all") {
        query = query.eq("pp_process_decision", value);
      }
    } else if (key === "pp_reason_code") {
      query = query.eq("pp_reason_code", value);
    } else if (typeof value === "string") {
      query = query.ilike(key, `%${value}%`);
    } else {
      query = query.eq(key, value);
    }
  });

  return query;
};

export const fetchArtifacts = async (
  params: ArtifactQueryParams
): Promise<ArtifactQueryResponse> => {
  try {
    const { page, pageSize, sortField, sortDirection, filters, permittedEmails } = params;
    const offset = page * pageSize;

    const { count, error: countError } = await applyFilters({
      query: supabase.from("vv_artifact").select("id", { count: "exact" }),
      filters,
      permittedEmails
    });

    if (countError) {
      logError("[fetchArtifacts] Error fetching count", countError);
      return {
        data: undefined,
        count: 0,
        error: countError
      };
    }

    const dataQuery = applyFilters({
      query: supabase.from("vv_artifact").select("*").range(offset, offset + pageSize - 1),
      filters,
      permittedEmails
    });

    const orderField = sortField ?? "created";
    const orderAscending = sortField ? sortDirection === "asc" : false;

    dataQuery.order(orderField, { ascending: orderAscending });
    dataQuery.order("id", { ascending: true });

    const { data, error } = await dataQuery;

    if (error) {
      logError("[fetchArtifacts] Error fetching artifacts", error);
      return {
        data: undefined,
        count: 0,
        error: error
      };
    }

    return {
      data: (data ?? []) as Artifact[],
      count: count ?? 0,
      error: null
    };
  } catch (error) {
    logError("[fetchArtifacts] Error", error);
    return {
      data: undefined,
      count: 0,
      error: error as any
    };
  }
};

interface FetchDistinctProtocolsParams {
  timeRange: TimeRange;
  permittedEmails: string[] | null;
}

export const fetchDistinctProtocols = async (
  params: FetchDistinctProtocolsParams
): Promise<string[]> => {
  const { timeRange, permittedEmails } = params;
  try {
    // Convert timeRange to actual dates
    const timeFilters = getTimeRangeDbFilters(timeRange);

    const { data, error } = await supabase
      .rpc("get_distinct_protocols", {
        start_date: timeFilters.created_from,
        end_date: timeFilters.created_to,
        permitted_emails: permittedEmails && permittedEmails.length > 0 ? permittedEmails.map(email => email.toLowerCase()) : undefined
      });

    if (error) {
      throw error;
    }

    return data || [];
  } catch (error) {
    logError("Error fetching distinct protocols", error);
    return [];
  }
};

interface FetchDistinctUsersParams {
  timeRange: TimeRange;
  permittedEmails: string[] | null;
}

export const fetchDistinctUsers = async (
  params: FetchDistinctUsersParams
): Promise<string[]> => {
  const { timeRange, permittedEmails } = params;

  try {
    // Convert timeRange to actual dates
    const timeFilters = getTimeRangeDbFilters(timeRange);

    const { data, error } = await supabase.rpc("get_distinct_users", {
      start_date: timeFilters.created_from,
      end_date: timeFilters.created_to,
      permitted_emails: permittedEmails && permittedEmails.length > 0 ? permittedEmails.map(email => email.toLowerCase()) : undefined
    });

    if (error) {
      throw error;
    }

    return data || [];
  } catch (error) {
    logError("Error fetching distinct users", error);
    return [];
  }
};

export const fetchDistinctAttributesForLens = async (
  lensName: string
): Promise<FetchResult<string[]>> => {
  try {
    const { data, error } = await supabase.rpc(
      "get_distinct_attributes_for_lens",
      { p_lens_name: lensName }
    );

    if (error) {
      logError("[fetchDistinctAttributesForLens] RPC error", error);
      return { data: [], error };
    }
    return { data: (data ?? []).map((row: { attribute: string }) => row.attribute), error: null };
  } catch (error) {
    logError("[fetchDistinctAttributesForLens] Error", error);
    return { data: [], error: normalizeError(error) };
  }
};

export interface FetchRankedCallsParams {
  lensName: string;
  order: RankingOrder;
  attributeName?: string;
  startDate?: string;
  endDate?: string;
  agentEmail?: string;
  resultLimit?: number;
  permittedEmails: string[] | null;
}

export const fetchRankedCalls = async (
  params: FetchRankedCallsParams
): Promise<FetchResult<Artifact[]>> => {
  try {
    const { data, error } = await supabase.rpc("get_ranked_calls", {
      p_lens_name: params.lensName,
      p_order: params.order,
      p_attribute_name: params.attributeName || undefined,
      p_start_date: params.startDate || undefined,
      p_end_date: params.endDate || undefined,
      p_agent_email: params.agentEmail || undefined,
      p_result_limit: params.resultLimit ?? DEFAULT_RANKING_COUNT,
      p_permitted_emails: params.permittedEmails && params.permittedEmails.length > 0
        ? params.permittedEmails.map(email => email.toLowerCase())
        : undefined
    });

    if (error) {
      logError("[fetchRankedCalls] RPC error", error);
      return { data: [], error };
    }

    return { data: (data ?? []) as Artifact[], error: null };
  } catch (error) {
    logError("[fetchRankedCalls] Error", error);
    return { data: [], error: normalizeError(error) };
  }
};

const pickSummaryFields = (item: Record<string, any>) => {
  const { type, content } = item;
  if (!R.includes(type, [ArtifactType.Summary, ArtifactType.Scorecard])) {
    return content;
  }
  return R.cond([
    [R.equals(ArtifactType.Summary), () => R.prop("extendedSummary", content)],
    [R.equals(ArtifactType.Scorecard), () => R.omit(["evaluation_process", "call_summary", "call_suitability", "scorecard_version", "scorecard_name"], content.scorecard)],
    [R.T, R.always(content)]
  ])(type);
};

/**
 * Fetches artifacts for a RAG interface to use with Claude
 * Retrieves selected artifact types for filtered artifacts
 * @param params Query parameters including filters
 * @param includedArtifactTypes Array of artifact types to include in the RAG context
 * @returns A promise that resolves to an array of artifacts in a format suitable for RAG
 */
/**
 * Fetches call recordings for a specific interaction ID
 * @param interactionId The interaction ID for which to fetch call recordings
 * @returns A promise that resolves to an object containing call recordings or an error
 */
export const fetchCallRecordings = async (
  interactionId: string
): Promise<FetchResult<any | null>> => {
  try {
    // Call the Supabase Function to fetch call recordings
    const { data, error } = await supabase.functions.invoke("get-call-recordings", {
      body: { interactionId }
    });

    if (error) {
      throw error;
    }

    return {
      data: {
        recordings: data?.recordings.map((rec: any) => ({
          id: rec.id,
          duration: rec.duration,
          url: rec.url,
          startedAt: rec.startedAt,
          mimeType: rec.mimeType || "audio/mpeg",
          channels: rec.channels || 1,
          fileSize: rec.fileSize || 0,
          speakers: rec.speakers || [],
          text: rec.text || ""
        }))
      },
      error: null
    };
  } catch (error) {
    logError("Error fetching call recordings", error);
    return {
      data: null,
      error: normalizeError(error)
    };
  }
};

export const fetchArtifactsForRag = async (
  params: ArtifactQueryParams,
  includedArtifactTypes: ArtifactType[] = [ArtifactType.Summary]
): Promise<FetchResult<any[] | null>> => {
  try {
    // Check if interactionIds are directly provided in params
    const interactionIds = params.interactionIds || [];

    // If interactionIds weren't provided directly, fetch filtered artifacts
    const finalInteractionIds = interactionIds.length > 0
      ? interactionIds
      : await (async () => {
        // Get the filtered list of artifacts
        const artifactsResult = await fetchArtifacts(params);

        if (artifactsResult.error || !artifactsResult.data) {
          throw artifactsResult.error || new Error("No artifacts found");
        }

        // Extract the interaction_ids from the filtered artifacts
        return artifactsResult.data.map(artifact => artifact.interaction_id);
      })();

    if (finalInteractionIds.length === 0) {
      return { data: [], error: null };
    }

    // Fetch the specific artifact types we need for each interaction
    // Split into batches of 100 to avoid URL length issues with large datasets
    const batches: string[][] = [];

    for (let i = 0; i < finalInteractionIds.length; i += ARTIFACT_BATCH_SIZE) {
      batches.push(finalInteractionIds.slice(i, i + ARTIFACT_BATCH_SIZE));
    }

    // Fetch all batches in parallel
    const batchPromises = batches.map(async (batchIds) => {
      const { data, error } = await supabase
        .from("artifact")
        .select("interaction_id, type, content")
        .in("interaction_id", batchIds)
        .in("type", includedArtifactTypes);

      if (error) {
        throw error;
      }

      return data || [];
    });

    const batchResults = await Promise.all(batchPromises);
    const data = batchResults.flat() as Array<{ interaction_id: string; type: string; content: any }>;

    if (!data || data.length === 0) {
      return { data: [], error: null };
    }

    // Group artifacts by interaction_id
    const groupedData = data.reduce((acc: Record<string, any>, item) => {
      if (!acc[item.interaction_id]) {
        acc[item.interaction_id] = {};
      }
      acc[item.interaction_id][item.type] = pickSummaryFields(item);
      return acc;
    }, {});

    // Convert to array format for RAG
    const ragData = Object.entries(groupedData).map(([interactionId, artifacts], index) => ({
      index: index + 1,
      interactionId,
      ...artifacts
    }));

    return { data: ragData, error: null };
  } catch (error) {
    logError("Error fetching artifacts for RAG", error);
    return {
      data: null,
      error: normalizeError(error)
    };
  }
};
